/**
 * Centralised plain-language UI copy for the MyPlayer GS1 Admin Console.
 * Keeps loading, empty, error, and accessibility strings in one module so all
 * pages present consistent, non-technical guidance.
 */

export const ADMIN_STRINGS = {
  navigation: {
    skipToMainContent: 'Skip to main content',
    railAriaLabel: 'Admin Navigation Rail',
    primaryNavAriaLabel: 'Primary Navigation',
    newProductButton: 'New Product / Identifier',
    signOutButton: 'Sign out',
    switchWorkspaceLabel: 'Switch Workspace',
    overviewLabel: 'Overview',
    productsLabel: 'Products',
    activityLabel: 'Activity',
    domainsLabel: 'Domains',
    settingsLabel: 'Settings',
    helpLabel: 'Help',
  },
  loading: {
    workspaceSession: 'Loading workspace session...',
    overview: 'Loading workspace overview and scan analytics...',
    products: 'Loading products and GS1 identifiers for this workspace...',
    domains: 'Loading resolver domains for this workspace...',
    auditLog: 'Loading workspace audit log entries...',
    workspaces: 'Loading organisation workspaces...',
    members: 'Loading organisation members...',
    apiKeys: 'Loading Partner API keys...',
  },
  empty: {
    overviewScansTitle: 'No scans yet',
    overviewScansDescription:
      'Once customers scan your printed QR codes, daily scan volume, top products, countries, and device breakdowns will appear here automatically.',
    overviewAttentionClear:
      'Everything looks healthy. No recalled products, broken links, or unpublished changes need attention.',
    productsCard:
      'No matching GS1 identifiers found in this workspace. Create a new product or clear your search filter.',
    productsTable:
      'No matching GS1 identifiers found for the current filters. Adjust your search or filter criteria above.',
    noIdentifierSelectedTitle: 'Identifier Details & Link Editor',
    noIdentifierSelectedDescription:
      'Select or create a GS1 identifier to configure target links and view draft vs live state.',
    noIdentifierSelectedBody:
      'No identifier selected. Click “New Product / Identifier” to stage a draft.',
    domains:
      'No custom domains registered for this workspace. Click Add Domain above to add and verify a brand domain.',
    auditLog:
      'No audit entries match the current filters for this workspace. Try broadening your search or filter selections.',
    workspaces: 'No workspaces found in this organisation.',
    members: 'No members found in this organisation.',
    apiKeys: 'No Partner API keys configured for this workspace.',
  },
  errors: {
    productsListenerPrefix: 'Could not load products:',
    productsListenerFallback:
      'We could not load your workspace products right now. Check your connection or permissions and try again.',
    domainsVerifyFallback:
      'We could not verify that custom domain right now. Check the hostname and your DNS TXT record, then try again.',
    auditLogFallback:
      'We could not load the workspace audit log right now. Check your connection or role permissions.',
    workspaceConfigFallback:
      'We could not save the workspace configuration. Check that the fallback URL starts with https:// and try again.',
    workspacesActionFallback: 'We could not complete that workspace change. Please try again.',
    membersActionFallback: 'We could not update that team membership. Please try again.',
    apiKeysActionFallback: 'We could not update that Partner API key. Please try again.',
    integrationsSaveFallback:
      'We could not save the Google Analytics integration. Check your Measurement ID and API secret and try again.',
    integrationsTestEventFallback:
      'We could not send the Google Analytics test event. Check your Measurement ID and API secret and try again.',
    signInEmailLinkFallback: 'We could not send the sign-in link. Check your email address.',
    signInPasswordFallback: 'Sign-in failed. Check your email and password and try again.',
    signInPasswordResetFallback:
      'We could not send the password reset email. Check your email address.',
    signInGoogleFallback: 'Google sign-in could not be completed. Please try again.',
    changePasswordFallback:
      'We could not update your password. Check your current password and make sure your new password is at least 8 characters.',
    resetPasswordActionFallback:
      'This password reset link is expired or has already been used. Request a new password reset email and try again.',
    verifyEmailActionFallback:
      'This verification link is expired or has already been used. Request a new verification email and try again.',
    verificationEmailFallback:
      'We could not send the verification email right now. Please wait a moment and try again.',
  },
  keyboard: {
    escapeClosesPanelHint: 'Press Escape to close this panel',
    tableRowSelectHint: 'Press Enter or Space to inspect this identifier',
  },
} as const;

const PERSONA_ROLE_LABELS: Readonly<Record<string, string>> = {
  SUPER_ADMIN: 'Super Admin',
  ORG_ADMIN: 'Admin',
  WORKSPACE_MANAGER: 'Workspace Manager',
  CAMPAIGN_EDITOR: 'Editor',
  COMPLIANCE_OFFICER: 'Compliance',
  VIEWER: 'Viewer',
  API_CLIENT: 'API Client',
};

/**
 * Formats an internal RBAC role identifier into a friendly label for brand staff.
 */
export function formatPersonaRoleLabel(role: string | null | undefined): string {
  if (!role) {
    return 'Member';
  }
  return PERSONA_ROLE_LABELS[role] ?? role;
}

/**
 * Formats an unknown error or raw error message into clear, plain language
 * while preserving any specific error code or actionable detail.
 */
export function formatPlainAdminError(err: unknown, fallbackMessage: string): string {
  const errCode =
    err !== null &&
    typeof err === 'object' &&
    typeof (err as { readonly code?: unknown }).code === 'string'
      ? (err as { readonly code: string }).code.trim()
      : '';
  const rawMessage =
    typeof err === 'string'
      ? err.trim()
      : err instanceof Error
        ? err.message.trim()
        : errCode.length > 0
          ? errCode
          : '';
  const combined = `${errCode} ${rawMessage}`.trim();

  if (combined.length === 0) {
    return fallbackMessage;
  }

  if (/auth\/(?:user-not-found|invalid-credential|wrong-password)/i.test(combined)) {
    return 'Incorrect email or password. Please check your details and try again.';
  }
  if (/auth\/invalid-email/i.test(combined)) {
    return 'Please enter a valid work email address (for example, name@company.com).';
  }
  if (/auth\/user-disabled/i.test(combined)) {
    return 'This account has been deactivated. Contact your workspace administrator for help.';
  }
  if (/auth\/too-many-requests/i.test(combined)) {
    return 'Too many attempts. Please wait a few minutes before trying again, or reset your password.';
  }
  if (/auth\/weak-password/i.test(combined)) {
    return 'Please choose a stronger password with at least 8 characters.';
  }
  if (/auth\/requires-recent-login/i.test(combined)) {
    return 'For your security, please enter your current password or sign in again before changing your password.';
  }
  if (/auth\/(?:expired-action-code|invalid-action-code)/i.test(combined)) {
    return 'This security link has expired or has already been used. Please request a fresh link and try again.';
  }
  if (/auth\/(?:popup-closed-by-user|cancelled-popup-request)/i.test(combined)) {
    return 'The Google sign-in window was closed before completing sign-in. Please try again.';
  }
  if (/auth\/popup-blocked/i.test(combined)) {
    return 'Your browser blocked the sign-in window. Please allow pop-ups for this site and try again.';
  }
  if (/auth\/email-already-in-use/i.test(combined)) {
    return 'An account with this email address already exists. Try signing in instead.';
  }
  if (/permission[_ -]?denied|insufficient permissions/i.test(combined)) {
    return 'You do not have permission to complete this action. Please contact your workspace administrator if you need access.';
  }
  if (/failed[_ -]?precondition|index building/i.test(combined)) {
    return 'The workspace database is finishing setup. Please wait a moment and refresh.';
  }
  if (/network|failed to fetch|offline|unavailable|auth\/network-request-failed/i.test(combined)) {
    return 'Network connection problem: please check your internet connection and try again.';
  }
  if (/unauthenticated/i.test(combined)) {
    return 'Authentication failed: please check your sign-in details and try again.';
  }

  return rawMessage
    .replace(/^Firebase:\s*/i, '')
    .replace(/\s*\(auth\/[a-z0-9-]+\)\.?/gi, '')
    .replace(/\s*\([A-Z0-9_]+\s*(?:—|-)\s*[^)]+\)/g, '')
    .replace(/\s*\[[A-Z0-9_]+\]/g, '')
    .replace(/\s*\([A-Z0-9_]{3,}\)/g, '')
    .trim();
}
